<template>
    <div>
        <vx-selectable
            :size="props.size"
            :modelValue="props.modelValue"
            @update:modelValue="emit('update:modelValue', $event)"
            :reduce="option => option.id"
            :multiple="props.multiple"
            @search="search"
            :placeholder="props.placeholder"
            :label="props.label"
            :options="options"
            :clearable="props.clearable"
            :deselectFromDropdown="true"
            :disabled="props.disabled"
            :relativeDropdown="props.relativeDropdown"
        >
        </vx-selectable>
    </div>
</template>

<script setup>
import { VxSelectable } from '@voxie/frontend-components';
import { campaignsClient } from '~/services';
import { computed, ref, onMounted, inject } from 'vue';
import debounce from '~/utils/debounce';
import { chunkArray } from '~/utils/array';
import { uniqBy } from '~/utils/helpers';

const teamId = Spark.state.currentTeam.id;

const IDS_PER_REQUEST = 10;

const props = defineProps({
    modelValue: {
    },
    disabled: {
        type: Boolean,
    },
    multiple: {
        type: Boolean,
    },
    clearable: {
        type: Boolean,
    },
    placeholder: {
        type: String,
        default: 'Select a campaign',
    },
    size: {
        type: String,
        default: 'md',
    },
    ids: {
        type: Array,
        default: () => ([])
    },
    flowId: {
        type: [String, undefined],
        default: undefined,
    },
    durationTypes: {
        type: Array,
        default: () => ([])
    },
    relativeDropdown: {
        type: Boolean,
    },
    label: {
        type: String,
        default: 'label',
    },
});

const FEATURES = inject('FEATURES')

const emit = defineEmits(["update:modelValue"]);

const placeholderOption = (id, name) => ({ id, name, label: name });
const withLabel = (campaign) => ({ ...campaign, label: `${campaign.id} - ${campaign.name}` });
const withDeletedName = (campaign) =>
    campaign.deleted_at ? { ...campaign, name: `${campaign.name} (deleted)` } : campaign;

// Selected ids resolve through an identity lookup; until then, and for ids it no longer returns, a
// placeholder keeps vue-select from rendering the bare id as the selection.
const selected = ref(props.ids.map((id) => placeholderOption(id, '')));
const searchResults = ref([]);

// A search hit outranks a placeholder for the same campaign, so a selection whose lookup failed
// still gets its name once the search returns it.
const options = computed(() => {
    const found = new Map(searchResults.value.map((campaign) => [campaign.id, campaign]));

    return uniqBy(
        [...selected.value.map((campaign) => found.get(campaign.id) ?? campaign), ...searchResults.value],
        (campaign) => campaign.id
    );
});

const search = debounce(async (search, loading) => {
    loading?.(true);

    try {
        if (props.ids.length) {
            // Naming what is already selected must not depend on the search's eligibility
            // filters, which would misreport a campaign that merely stopped qualifying.
            const byId = new Map();
            const failedIds = new Set();
            await Promise.all(
                chunkArray([...new Set(props.ids)], IDS_PER_REQUEST).map(async (batch) => {
                    try {
                        const idsResponse = await campaignsClient.campaignsGetRecords(teamId, {
                            ids: batch,
                            per_page: batch.length,
                            with_trashed: 1,
                        });
                        idsResponse.data.data.forEach((campaign) =>
                            byId.set(campaign.id, withLabel(withDeletedName(campaign)))
                        );
                    } catch {
                        batch.forEach((id) => failedIds.add(id));
                    }
                })
            );
            selected.value = props.ids.map(
                (id) =>
                    byId.get(id) ?? placeholderOption(id, failedIds.has(id) ? 'Unavailable campaign' : 'Deleted campaign')
            );
        } else {
            selected.value = [];
        }

        try {
            const response = await campaignsClient.campaignsGetRecords(teamId, {
                sort_field: 'id',
                sort_dir: 'desc',
                query: search,
                duration_types: props.durationTypes.length ? props.durationTypes : undefined,
                flow: FEATURES.flows ? {
                    in_flow: props.flowId ? 1 : 0,
                    flow_id: props.flowId,
                } : undefined
            });
            searchResults.value = response.data.data.map(withLabel);
        } catch {
            // The selection still renders from the identity lookup.
        }
    } finally {
        loading?.(false);
    }
}, 300);

onMounted(() => search());
</script>
